import React, { useEffect, useState } from "react";
import axios from "axios";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, Loader2, Fingerprint, Leaf, Vote, Server } from "lucide-react";
import { toast } from "sonner";

const f0 = (n) => (typeof n === "number" ? Math.round(n).toLocaleString() : "—");
const COUPON_ROLES = ["public_authority", "collective_executive", "admin"];

export const CouponPanel = ({ c, onChanged }) => {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/renewal/cases/${c.id}/coupons`).then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => { load(); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!c.bond) return null;
  const run = async (all) => {
    setBusy(true);
    try {
      const { data } = await api.post(all ? "/renewal/coupon/run-all" : `/renewal/cases/${c.id}/coupon/run`);
      toast.success(all ? `${data.runs.length} coupon run(s) · ${f0(data.paid_try)} TRY paid · ${data.skipped} skipped` : `${data.period} coupon · ${f0(data.paid_try)} TRY to ${data.payouts.length} holder(s) · coverage ${Math.round(data.coverage * 100)}%`);
      load(); onChanged();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="qs-panel p-3 text-xs" data-testid="coupon-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Coins className="h-3 w-3" /> Coupon payments · {c.bond.coupon_pct}% p.a. paid monthly from rent income · {runs.length} run(s)</div>
        {user && COUPON_ROLES.includes(user.role) && ["construction", "completed"].includes(c.status) && (
          <div className="flex gap-1.5">
            <button data-testid="coupon-run" disabled={busy} onClick={() => run(false)} className="border border-[#00FF7F]/60 text-[#00FF7F] px-3 py-1 disabled:opacity-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Run this month's coupon"}</button>
            <button data-testid="coupon-run-all" disabled={busy} onClick={() => run(true)} className="border border-white/15 text-slate-300 px-3 py-1 disabled:opacity-50">Run all due coupons</button>
          </div>
        )}
      </div>
      {runs.map((r) => (
        <div key={r.id} className="border-t border-white/5 py-1.5" data-testid="coupon-run-row">
          <div className="flex flex-wrap justify-between gap-2 font-mono"><span className="text-slate-200">{r.period} · {f0(r.paid_try)} / {f0(r.due_try)} TRY · coverage {Math.round(r.coverage * 100)}%{r.shortfall_try > 0 && <span className="text-[#F59E0B]"> · shortfall {f0(r.shortfall_try)}</span>}</span>
            <span className="text-[#00FF7F] flex items-center gap-1"><Leaf className="h-3 w-3" />{r.zero_carbon.zcc_minted ? `${r.zero_carbon.zcc_minted} ZCC` : "no carbon claim"}</span></div>
          <div className="text-slate-500">{r.payouts.map((p) => `${p.holder} ${f0(p.paid_try)}`).join(" · ")} · rent {f0(r.rent_income_try)}/mo</div>
          <div className="text-[10px] font-mono text-slate-600 flex items-center gap-1"><Fingerprint className="h-3 w-3" />{r.anchor.slice(0, 20)}… · {r.zero_carbon.credibility}</div>
        </div>
      ))}
      {!runs.length && <div className="text-slate-600">No coupon distributed yet.</div>}
    </div>
  );
};

export const ConsentVote = ({ c, onChanged }) => {
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => axios.get(`${API}/renewal/cases/${c.id}/consent`).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const canVote = user && (c.owner === (user.name || user.email) || ["admin", "public_authority"].includes(user.role));
  const cast = async (i, vote) => {
    setBusy(i);
    try { const { data } = await api.post(`/renewal/cases/${c.id}/consent`, { owner_index: i, vote }); setSt(data); toast.success(`Vote anchored · ${data.yes_units}/${data.required_units} units`); onChanged(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  if (!st || !c.title_deed) return null;
  const votes = Object.fromEntries(st.votes.map((v) => [v.owner_index, v.vote]));
  return (
    <div className="bg-[#050A15] border border-white/5 p-3 text-xs" data-testid="consent-vote">
      <div className="flex flex-wrap justify-between gap-2"><div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Vote className="h-3 w-3" /> Law 6306 owner consent · two-thirds of units</div>
        <span className="font-mono" style={{ color: st.reached ? "#00FF7F" : "#F59E0B" }} data-testid="consent-status">{st.yes_units}/{st.required_units} units yes · {st.no_units} no · {st.reached ? "CONSENT REACHED" : "not yet"}</span></div>
      <div className="h-1.5 bg-white/5 mt-1"><div className="h-full bg-[#00FF7F] transition-all" style={{ width: `${Math.min(100, st.yes_units / st.required_units * 100)}%` }} /></div>
      <div className="flex flex-wrap gap-1 mt-2">
        {c.title_deed.owners.map((o, i) => (
          <div key={`${o.owner}-${o.share}`} className="border border-white/10 px-1.5 py-0.5 flex items-center gap-1" data-testid={`consent-owner-${i}`}>
            <span className="text-slate-300">{o.owner} <span className="text-slate-500">{o.share}</span></span>
            {votes[i] && <span className="font-mono" style={{ color: { yes: "#00FF7F", no: "#EF4444", abstain: "#94a3b8" }[votes[i]] }}>{votes[i]}</span>}
            {canVote && !votes[i] && <span className="flex gap-0.5">{["yes", "no", "abstain"].map((v) => <button key={v} data-testid={`consent-${i}-${v}`} disabled={busy === i} onClick={() => cast(i, v)} className="text-[9px] uppercase px-1 border border-white/10 hover:border-[#F59E0B] hover:text-[#F59E0B]">{v[0]}</button>)}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const RhinoPanel = ({ caseId }) => {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [solver, setSolver] = useState(null);
  const [form, setForm] = useState({ url: "", api_key: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/renewal/settings/rhino`).then((r) => { setS(r.data); setForm((f) => ({ ...f, url: r.data.url || "" })); }).catch(() => {});
    axios.get(`${API}/renewal/cases/${caseId}/retrofit`).then((r) => setSolver(r.data.solver)).catch(() => {});
  }, [caseId]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post("/renewal/settings/rhino", form); setS(data); toast.success(data.url ? "Rhino.Compute endpoint saved — next solve tries the live server" : "Live solver disabled — emulation only"); const r = await axios.get(`${API}/renewal/cases/${caseId}/retrofit`); setSolver(r.data.solver); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#050A15] border border-white/5 p-3 text-xs space-y-1.5" data-testid="rhino-panel">
      <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Server className="h-3 w-3" /> Solver</div>
      <div className="font-mono" data-testid="solver-badge"><span className="px-1.5 py-0.5 border" style={{ color: solver?.solver === "rhino.compute" ? "#00FF7F" : "#F59E0B", borderColor: solver?.solver === "rhino.compute" ? "#00FF7F66" : "#F59E0B66" }}>{solver?.solver === "rhino.compute" ? "LIVE · Rhino.Compute" : "EMULATED · built-in"}</span> <span className="text-slate-500">{solver?.status}</span></div>
      {user?.role === "admin" && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          <input data-testid="rhino-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://compute.example.com" className="flex-1 min-w-[180px] bg-transparent border border-white/10 px-2 py-1 outline-none focus:border-[#00FFFF]" />
          <input data-testid="rhino-key" type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="RhinoComputeKey" className="w-36 bg-transparent border border-white/10 px-2 py-1 outline-none" />
          <button data-testid="rhino-save" disabled={busy} onClick={save} className="border border-[#00FFFF]/60 text-[#00FFFF] px-3 py-1 disabled:opacity-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}</button>
          {s?.has_key && <span className="text-slate-500 self-center">key stored server-side</span>}
        </div>
      )}
    </div>
  );
};
